import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Toolbar,
  Button,
  useMediaQuery,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Fab,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useHistory, useLocation } from 'react-router-dom';
import { useAccount } from './../../contexts/Account';
import eventBus, { eventBusValues } from './../../eventBus';
import InviteContactsModal from '../Advisor/TopBar/InviteContactsModal';
import ACSnackbar from '../ACSnackbar';
import TopSection from './TopSection';

const useStyles = () => ({
  root: {
    alignItems: 'center',
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: 0.75,
    lineHeight: '32px',
    width: '100%',
    backgroundColor: '#F4F8FF',
    zIndex: '99',
  },
  outer: {
    height: '65px',
  },
  mainButton: {
    width: '205px',
    height: '48px',
    borderRadius: '25px',
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '24px',
    letterSpacing: '-0.43px',
    textAlign: 'left',
    textTransform: 'none',
    boxShadow: 'none',
  },
});

function ButtonItem(text, path, history, location, id, isBeta, freemiumGate) {
  const { isFreemium } = useAccount();
  const isCurrentPath = location.pathname === path;

  return (
    <Box sx={{ position: 'relative' }} key={path}>
      <Button
        variant="text"
        onClick={() => {
          if (freemiumGate && isFreemium) {
            eventBus.dispatch(eventBusValues.triggerCompanyUpgradeAction);
          } else {
            history.push(path);
          }
        }}
        sx={{
          textTransform: 'none',
          height: '48px',
          fontStyle: 'normal',
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '18px',
          letterSpacing: '0.75px',
          borderRadius: '8px',
          px: '20px',
          color: isCurrentPath ? '#001030' : '#4D586F',
          backgroundColor: isCurrentPath ? '#DEE9FE' : 'transparent',
          '&:hover': {
            backgroundColor: isCurrentPath ? '#DEE9FE' : 'transparent',
          },
        }}
        id={id}
      >
        {text}
        {isBeta && (
          <Fab
            variant="extended"
            size="small"
            color="secondary"
            role="none"
            component="div"
            style={{
              height: 18,
              position: 'absolute',
              top: 0,
              right: -30,
              color: '#1976d2',
              fontSize: 9,
              fontWeight: 'bold',
              paddingTop: 2,
              boxShadow: 'none',
              backgroundColor: '#E7EDFD',
              zIndex: 0,
            }}
          >
            {' '}
            Beta
          </Fab>
        )}
      </Button>
    </Box>
  );
}

const SearchPagesHeader = ({ boxRef, isTopMatches }) => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const [sticky, setSticky] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [inviteSnackbarOpen, setInviteSnackbarOpen] = useState(false);
  const { accountDetails, isFreemium } = useAccount();
  const navRef = useRef();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const buttonsData = !isFreemium
    ? [
        {
          text: `Your ${
            accountDetails.userSegment === 'managed_advisory_board'
              ? 'Advisory Board'
              : 'AdvisoryCloud'
          }`,
          path: '/advisors',
          id: 'b2bAdvisorsSubMenu',
        },
        {
          text: 'Advisor Directory',
          path: '/advisors/search',
          id: 'b2bSearchAdvisorsSubMenu',
        },
        {
          text: 'Saved',
          path: '/advisors/saved',
          id: 'b2bSavedAdvisorsSubMenu',
        },
        {
          text: 'Top Matches',
          path: '/advisors/top_matches',
          id: 'b2bTopMatchesAdvisorsSubMenu',
          // freemiumGate: !isImpersonated,
        },
      ]
    : [
        {
          text: `Your ${
            accountDetails.userSegment === 'managed_advisory_board'
              ? 'Advisory Board'
              : 'AdvisoryCloud'
          }`,
          path: '/advisors',
          id: 'b2bAdvisorsSubMenu',
        },
        {
          text: 'Our Network',
          path: '/advisors/search',
          id: 'b2bSearchAdvisorsSubMenu',
        },
        {
          text: 'Top Matches',
          path: '/advisors/top_matches',
          id: 'b2bTopMatchesAdvisorsSubMenu',
          // freemiumGate: !isImpersonated,
        },
        {
          text: 'Saved',
          path: '/advisors/saved',
          id: 'b2bSavedAdvisorsSubMenu',
        },
      ];
  const routeTextMap = {
    '/advisors': `Your ${
      accountDetails.userSegment === 'managed_advisory_board'
        ? 'Advisory Board'
        : 'AdvisoryCloud'
    }`,
    '/advisors/search': 'Advisor Directory',
    '/advisors/saved': 'Saved',
    '/advisors/top_matches': 'Top Matches',
  };

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
    switch (event.target.value) {
      case 'advisors':
        history.push('/advisors');
        break;
      case 'search':
        history.push('/advisors/search');
        break;
      case 'saved':
        history.push('/advisors/saved');
        break;
      case 'top_matches':
        history.push('/advisors/top_matches');
        break;
      default:
        break;
    }
  };

  const handleShowInviteSnackbar = () => {
    setInviteSnackbarOpen(true);
  };

  function onScroll() {
    if (navRef && navRef.current && navRef.current.getBoundingClientRect) {
      setSticky(navRef.current.getBoundingClientRect().top < 40);
    }
  }

  useEffect(() => {
    const path = location.pathname;
    if (path === '/advisors') {
      setSelectedOption('advisors');
    } else if (path === '/advisors/search') {
      setSelectedOption('search');
    } else if (path === '/advisors/saved') {
      setSelectedOption('saved');
    } else if (path === '/advisors/top_matches') {
      setSelectedOption('top_matches');
    }
  }, [location]);

  useEffect(() => {
    if (boxRef && boxRef.current) {
      boxRef.current.addEventListener('scroll', onScroll);
    }

    return () => {
      if (boxRef && boxRef.current) {
        boxRef.current.removeEventListener('scroll', onScroll);
      }
    };
  }, []);

  return (
    <>
      <Box pr={isSM ? '10px' : '30px'} pl={isSM ? '10px' : '30px'} pt="30px">
        <TopSection />
      </Box>

      <div
        style={{ display: 'flex', width: '1px', height: '0px' }}
        ref={navRef}
      />
      <Box
        sx={{
          ...classes.root,
          position: sticky ? 'fixed' : null,
          top: '60px',
          width: '100%',
          marginTop: sticky ? '0px' : '20px',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            paddingLeft: { xxs: '0px', sm: '30px' },
            paddingRight: { xxs: '0px' },
            maxWidth: '1260px',
          }}
        >
          {!isSM &&
            buttonsData.map(({ text, path, id, isBeta, freemiumGate }) =>
              ButtonItem(
                text,
                path,
                history,
                location,
                id,
                isBeta,
                freemiumGate
              )
            )}

          {isSM && (
            <Select
              labelId="filter-label"
              id="advisors-mobile-menu"
              value={selectedOption}
              onChange={handleOptionChange}
              renderValue={() => routeTextMap[location.pathname] || ''}
              sx={{
                height: '40px',
                fontSize: '14px',
                color: '#232B3',
                fontWeight: 400,
                padding: '5px',
                height: '44px',
                width: '100%',
                m: sticky ? '0px 17px 0px 10px' : '0px 10px',
                borderRadius: '15px',
                backgroundColor: '#FFF',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid #E6EDFF',
                },
              }}
            >
              <MenuItem value="advisors" id="advisors-mobile-ac">
                Your AdvisoryCloud
              </MenuItem>
              <MenuItem value="search" id="advisors-mobile-search">
                Advisor Directory
              </MenuItem>
              <MenuItem value="saved" id="advisors-mobile-saved">
                Saved
              </MenuItem>
              <MenuItem value="top_matches" id="advisors-mobile-top">
                Top Matches
                <Fab
                  variant="extended"
                  size="small"
                  color="secondary"
                  role="none"
                  // onClick={handleMenuItem}
                  style={{
                    height: 18,
                    position: 'relative',
                    top: -12,
                    right: 10,
                    color: '#1976d2',
                    fontSize: 9,
                    fontWeight: 'bold',
                    paddingTop: 2,
                    boxShadow: 'none',
                    backgroundColor: '#E7EDFD',
                  }}
                >
                  Beta
                </Fab>
              </MenuItem>
            </Select>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginLeft: 'auto',
            }}
          >
            {!isSM && isTopMatches && (
              <>
                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '24px',
                    letterSpacing: '-0.43px',
                    color: '#646D7A',
                  }}
                >
                  Why am I seeing these matches?
                </Typography>
                <Tooltip
                  id="topMatchesInfoTooltip"
                  title="Top Matched advisors are based on the Skills and Desired Industries selections on your profile. To see different advisors, update those attributes on your profile. You can also manually search advisors in the “Search Network” tab. "
                >
                  <IconButton
                    sx={{
                      padding: '0px',
                      marginLeft: '5px',
                      marginRight: '30px',
                      backgroundColor: 'black',
                      height: '14px',
                      width: '14px',
                    }}
                    id="topMatchesInfo"
                  >
                    <InfoIcon
                      sx={{
                        color: '#D7F1FD',
                        fontSize: '18px',
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Toolbar>
      </Box>
      {sticky && <Box sx={classes.outer} />}
      <ACSnackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={inviteSnackbarOpen}
        autoHideDuration={6000}
        severity="success"
        text="Your contacts have been invited to your AdvisoryCloud"
        onClose={() => setInviteSnackbarOpen(false)}
      />
      <InviteContactsModal onInviteSent={handleShowInviteSnackbar} />
    </>
  );
};

export default SearchPagesHeader;
